import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, MoreVertical, PlusSquare, CheckCircle2, Share, MonitorDown, MoreHorizontal } from 'lucide-react';
import { Button } from 'components/ui/button';

export function InstallPromptModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [os, setOs] = useState('');

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    const isAndroid = /android/i.test(navigator.userAgent);
    const isIOS = /ipad|iphone|ipod/i.test(navigator.userAgent) && !window.MSStream;
    
    if (isAndroid) setOs('android');
    else if (isIOS) setOs('ios');
    
    const hasSeenPrompt = localStorage.getItem('hasSeenInstallPrompt');

    if ((isAndroid || isIOS) && !isStandalone && !hasSeenPrompt) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('hasSeenInstallPrompt', 'true');
  };

  if (!isOpen) return null;

  const content = {
    android: [
      {
        icon: <MoreVertical className="w-8 h-8" />,
        text: <>Toque nos <strong className="text-gray-900 dark:text-white">3 pontinhos (⋮)</strong> no canto superior direito do Chrome.</>
      },
      {
        icon: <MonitorDown className="w-8 h-8" />,
        text: <>Procure e toque em <strong className="text-gray-900 dark:text-white">Instalar app</strong> ou <strong className="text-gray-900 dark:text-white">Adicionar à tela inicial</strong>.</>
      },
      {
        icon: <CheckCircle2 className="w-8 h-8" />,
        text: <>Confirme tocando em <strong className="text-gray-900 dark:text-white">Instalar</strong> ou <strong className="text-gray-900 dark:text-white">Adicionar</strong>. Pronto!</>
      }
    ],
    ios: [
      {
        icon: <MoreHorizontal className="w-8 h-8" />,
        text: <>Toque nos <strong className="text-gray-900 dark:text-white">3 pontinhos (...)</strong> na barra inferior do Safari.</>
      },
      {
        icon: <PlusSquare className="w-8 h-8" />,
        text: <>Selecione <strong className="text-gray-900 dark:text-white">Compartilhar</strong> e depois <strong className="text-gray-900 dark:text-white">Adicionar à Tela de Início</strong>.</>
      },
      {
        icon: <CheckCircle2 className="w-8 h-8" />,
        text: <>No canto superior direito, confirme tocando em <strong className="text-gray-900 dark:text-white">Adicionar</strong>. Pronto!</>
      }
    ]
  };

  const currentContent = content[os] || content['android'];
  const currentStepData = currentContent[step - 1];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-300">
      <div 
        className="bg-white dark:bg-card rounded-xl shadow-2xl relative overflow-hidden flex flex-col"
        style={{ width: '90%', maxWidth: '400px', minWidth: '320px' }}
      >
        <div className="absolute top-5 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          <div className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${step >= 1 ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
          <div className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${step >= 2 ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
          <div className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${step >= 3 ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
        </div>

        <div className="flex justify-between items-center p-4 pt-12 border-b border-gray-100 dark:border-border">
          <h3 className="font-bold text-xl text-gray-900 dark:text-white mx-auto">Instalar Aplicativo</h3>
          <button onClick={handleClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1 bg-gray-100 dark:bg-gray-800 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex flex-col items-center text-center min-h-[220px] justify-center relative bg-white dark:bg-card">
          <div key={step} className="animate-in slide-in-from-right-4 fade-in duration-300 flex flex-col items-center w-full">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 text-green-600 dark:text-green-400">
              {currentStepData.icon}
            </div>
            <p className="text-gray-700 dark:text-gray-300 font-medium text-lg leading-relaxed">
              {currentStepData.text}
            </p>
          </div>
        </div>

        <div className="p-5 bg-gray-50 dark:bg-muted/30 flex justify-between items-center gap-4 border-t border-gray-100 dark:border-border">
          <Button 
            variant="outline" 
            onClick={() => setStep(step - 1)} 
            disabled={step === 1}
            className="flex-1 py-6 text-base font-semibold"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Voltar
          </Button>
          
          {step < 3 ? (
            <Button 
              className="flex-1 py-6 text-base font-semibold bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700"
              onClick={() => setStep(step + 1)}
            >
              Avançar
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          ) : (
            <Button 
              className="flex-1 py-6 text-base font-semibold bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700"
              onClick={handleClose}
            >
              Entendi!
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
